import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import { seriesList } from "@/mocks/siteData";

const statusTabs = ["全部", "连载中", "已完结"] as const;

export default function Series() {
  const [status, setStatus] = useState<string>("全部");
  const list = seriesList.filter((s) => status === "全部" || s.status === status);

  return (
    <section id="series" className="bg-background-50 py-16 md:py-24 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 uppercase">
                <span className="w-6 h-px bg-primary-400"></span>
                全集在线
              </span>
              <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
                剧集更新进度一目了然
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                连载类内容最怕追到一半断了节奏。这里按更新状态整理，标注每部剧的总集数与当前进度，
                方便判断是现在就能一口气看完，还是需要跟着更新节奏走。
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
              {statusTabs.map((t) => (
                <button
                  key={t}
                  onClick={() => setStatus(t)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    status === t ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 bg-background-100 rounded-lg border border-background-200 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3.5 bg-background-200/70 text-xs font-medium text-foreground-500">
            <span className="col-span-3">剧集名称</span>
            <span className="col-span-4">更新进度</span>
            <span className="col-span-3">说明</span>
            <span className="col-span-2 text-right">状态</span>
          </div>

          <div className="divide-y divide-background-200">
            {list.map((s, idx) => {
              const percent = Math.round((s.updated / s.total) * 100);
              return (
                <Reveal key={s.id} delay={Math.min(idx, 5) * 50}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-5 md:px-6 py-5 items-center hover:bg-background-50 transition-colors">
                    <div className="md:col-span-3 flex items-center gap-3">
                      <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                        <i className="ri-play-list-2-line text-lg" aria-hidden="true"></i>
                      </span>
                      <div>
                        <p className="font-heading text-base font-semibold text-foreground-950">{s.title}</p>
                        <p className="text-xs text-foreground-500">共 {s.total} 集</p>
                      </div>
                    </div>

                    <div className="md:col-span-4">
                      <div className="flex items-center justify-between text-xs text-foreground-600 mb-1.5">
                        <span>{s.progress}</span>
                        <span className="font-medium text-foreground-900">{percent}%</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-background-300 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${percent === 100 ? "bg-accent-500" : "bg-primary-500"}`}
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                    </div>

                    <p className="md:col-span-3 text-xs md:text-sm text-foreground-600 leading-relaxed">{s.desc}</p>

                    <div className="md:col-span-2 flex md:justify-end">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          s.status === "连载中"
                            ? "bg-primary-100 text-primary-800"
                            : "bg-secondary-100 text-secondary-800"
                        }`}
                      >
                        <i
                          className={s.status === "连载中" ? "ri-loader-4-line" : "ri-check-double-line"}
                          aria-hidden="true"
                        ></i>
                        {s.status}
                      </span>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={80}>
          <div className="mt-6 flex items-start gap-2.5 text-xs md:text-sm text-foreground-500 leading-relaxed">
            <i className="ri-information-line text-base text-secondary-500 mt-0.5 shrink-0" aria-hidden="true"></i>
            <p>进度数据用于对照更新节奏，实际可播放集数以播放页为准；连载中的条目会随更新同步调整。</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}